import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, TrendingUp, Target, BarChart3, Play, Monitor, Store, RefreshCw, Award, Users, LineChart, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AmazonPPC = () => {
  const whyChooseUs = [
    { icon: Users, title: 'Experienced Amazon PPC Specialists', description: 'Our team has deep expertise in Amazon advertising across all campaign types.' },
    { icon: BarChart3, title: 'Data-Driven Advertising Strategies', description: 'Every decision is backed by comprehensive data analysis and insights.' },
    { icon: RefreshCw, title: 'Continuous Campaign Optimization', description: 'We monitor and optimize campaigns daily for peak performance.' },
    { icon: TrendingUp, title: 'Focus on Profitable Growth & ROI', description: 'Our goal is maximizing your return on every advertising rupee spent.' }
  ];

  const keyMetrics = [
    { name: 'ACOS', description: 'Advertising Cost of Sales' },
    { name: 'TACOS', description: 'Total Advertising Cost of Sales' },
    { name: 'CTR', description: 'Click-Through Rate' },
    { name: 'Conversion Rate', description: 'Sales per Click' },
    { name: 'Keyword Performance', description: 'Search Term Analytics' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Amazon PPC Management & Advertising Services | Obiquiste</title>
        <meta name="description" content="Expert Amazon PPC management: Sponsored Products, Brands & Display ads. Reduce ACoS, increase ROI. Data-driven Amazon advertising agency in India." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/amazon-ppc-management-advertising" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/amazon-account-management-services" className="text-slate-700 hover:text-primary font-medium">Amazon</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">PPC Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Amazon PPC Management & <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">Advertising Services</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Drive Visibility. Maximize Conversions. Achieve Profitable Growth.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                At Obiquiste Solutions, we help businesses create, manage, and optimize Amazon advertising campaigns to increase product visibility, improve conversions, and achieve profitable growth with maximum ROI.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=amazon-ppc">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free PPC Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=amazon-ppc">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to PPC Expert
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction to Amazon PPC Advertising */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <BarChart3 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Introduction to Amazon PPC Advertising
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Amazon advertising is one of the most powerful tools for brands looking to increase visibility, drive targeted traffic, and boost sales on the world's largest e-commerce marketplace. With millions of products competing for attention, strategic PPC campaigns help your products stand out when customers are actively searching.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              At Obiquiste Solutions, our goal is to ensure <strong>every advertising rupee spent contributes toward profitable growth and improved ROI</strong>. We combine data-driven strategies with continuous optimization to deliver measurable results for your Amazon business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sponsored Products Campaign Management */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-xl mb-6">
                <Target className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Sponsored Products Campaign Management
              </h2>
              <p className="text-lg text-slate-700 mb-6">
                Sponsored Products are keyword-targeted ads that appear in Amazon search results and product pages. We help create and optimize these campaigns to promote your product listings and drive qualified traffic to your products.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Services Include:</h3>
                <div className="space-y-4">
                  {[
                    'Campaign strategy and structure planning',
                    'Keyword research and competitor analysis',
                    'Automatic and manual campaign setup',
                    'Bid optimization and budget management',
                    'Search term analysis and keyword harvesting',
                    'Negative keyword implementation',
                    'Continuous performance optimization'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Targeting Campaign Management */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Strategies Include:</h3>
                <div className="space-y-4">
                  {[
                    'Competitor ASIN targeting',
                    'Category targeting',
                    'Strategic bid optimization',
                    'Performance monitoring',
                    'Scaling high-performing targets'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-6">
                <Zap className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Targeting Campaign Management
              </h2>
              <p className="text-lg text-slate-700 mb-6">
                Product targeting allows your ads to appear on competitor product pages and related listings. This strategic approach helps capture customers who are actively comparing products, giving you the opportunity to win their business at the moment of decision.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sponsored Brands Campaign Management */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <Award className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Sponsored Brands Campaign Management
              </h2>
              <p className="text-lg text-slate-700 mb-6">
                Sponsored Brands help increase brand visibility by displaying your brand logo and multiple products at the top of Amazon search results. These premium placements build brand awareness and drive customers to your product catalog or Amazon Brand Store.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Services Include:</h3>
                <div className="space-y-4">
                  {[
                    'Campaign setup and optimization',
                    'Brand creative optimization',
                    'Keyword targeting and bid management',
                    'Product selection for ads',
                    'Campaign performance monitoring'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sponsored Brand Video Campaigns */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Services Include:</h3>
                <div className="space-y-4">
                  {[
                    'Video campaign strategy',
                    'Keyword targeting',
                    'Bid optimization',
                    'Campaign performance monitoring',
                    'Creative recommendations'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-xl mb-6">
                <Play className="w-7 h-7 text-red-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Sponsored Brand Video Campaigns
              </h2>
              <p className="text-lg text-slate-700 mb-6">
                Video ads appear within Amazon search results and help capture customer attention with engaging visual content. Video campaigns are highly effective for demonstrating product features, building brand trust, and driving higher engagement rates compared to static ads.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Display Advertising */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Monitor className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Amazon Display Advertising
              </h2>
              <p className="text-lg text-slate-700 mb-6">
                Display ads help retarget customers and increase brand visibility across Amazon and partner websites. These ads reach shoppers who have viewed your products or similar items, keeping your brand top-of-mind throughout their purchase journey.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Services Include:</h3>
                <div className="space-y-4">
                  {[
                    'Audience targeting',
                    'Product targeting',
                    'Retargeting campaigns',
                    'Performance optimization',
                    'Cross-platform reach strategy'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Brand Store Advertising */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Services Include:</h3>
                <div className="space-y-4">
                  {[
                    'Brand Store design and structure',
                    'Product catalog organization',
                    'Store optimization',
                    'Sponsored Brand campaigns driving store traffic',
                    'Performance analytics and reporting'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-100 rounded-xl mb-6">
                <Store className="w-7 h-7 text-indigo-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Amazon Brand Store Advertising
              </h2>
              <p className="text-lg text-slate-700 mb-6">
                We help create and optimize Amazon Brand Stores and run advertising campaigns that drive traffic to your store. A well-designed Brand Store combined with strategic advertising creates a powerful branded shopping experience that increases customer trust and conversion rates.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Continuous Campaign Optimization */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <RefreshCw className="w-16 h-16 mx-auto mb-6 text-primary" />
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Continuous Campaign Optimization
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Amazon PPC requires continuous monitoring and optimization to maintain performance and profitability. We track key metrics daily and make data-driven adjustments to maximize your advertising ROI.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {keyMetrics.map((metric, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center"
              >
                <LineChart className="w-8 h-8 text-primary mx-auto mb-3" />
                <p className="font-bold text-lg mb-1">{metric.name}</p>
                <p className="text-sm text-slate-400">{metric.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Why Choose Obiquiste Solutions
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Partner with Amazon PPC experts who are committed to your profitable growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-start space-x-4 p-6 bg-slate-50 rounded-2xl border border-slate-200"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              Our Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Amazon Brands We Currently Work With
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We manage PPC campaigns for 20+ brands across diverse categories, delivering profitable advertising results.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Brand</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Product / Focus</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Category</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { brand: 'Nutri Hut', product: 'FMCG / Nutrition Products', category: 'FMCG / Food & Nutrition' },
                  { brand: 'Rayon Caboki', product: 'Hair/Beauty Products', category: 'Beauty & Personal Care / Hair Care' },
                  { brand: 'Ayurdaanta Herbs', product: 'Ayurvedic Herbs & Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Ayur Devam', product: 'Ayurvedic/FMCG Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Suparna Trikha', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Haute', product: 'Gas Stoves', category: 'Home & Kitchen / Kitchen Appliances' },
                  { brand: 'GK Trading', product: 'Wallpapers', category: 'Home Improvement / Home Décor' },
                  { brand: 'Yuri Woori', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Mooncntrl', product: 'Apparel', category: 'Fashion / Apparel' },
                  { brand: 'Indian Kitchen Foods', product: 'Ready-to-Eat Meals', category: 'FMCG / Packaged Food & Ready-to-Eat' },
                  { brand: 'Earthvya', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'GW Khamkar', product: 'FMCG Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Vintage Oils', product: 'Oils / Edible Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Fitelo', product: 'Smart Weighing Scales', category: 'Electronics / Smart Health Devices' },
                  { brand: 'Fitty', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'RajGharana Metals', product: 'Kitchen Utensils', category: 'Home & Kitchen / Cookware & Kitchenware' },
                  { brand: 'HII Health Inside It', product: 'Health & Wellness Products', category: 'Health & Wellness' },
                  { brand: 'Auli Lifestyle', product: 'Skincare & Beauty Products', category: 'Beauty & Personal Care / Skincare' },
                  { brand: 'EHOP', product: 'TV & Electronic Remotes', category: 'Electronics / Consumer Electronics Accessories' },
                  { brand: 'Lavenust', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                ].map((item, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 hover:bg-orange-50/50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <span className="font-semibold text-slate-800">{item.brand}</span>
                    </td>
                    <td className="py-4 px-6 text-slate-600">{item.product}</td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {item.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-12 border border-orange-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Scale Your Amazon Advertising?
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              Let our PPC experts create and optimize campaigns that drive visibility, conversions, and profitable growth for your Amazon business.
            </p>
            <Link to="/contact?service=amazon-ppc">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free PPC Audit
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonPPC;
